import React, { useContext } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Trophy,
  Award,
  Gift,
  Sparkles,
  TrendingUp,
  Users2,
  Target,
  Flame,
  Heart,
  Rocket,
  Crown,
  Medal,
  Coins,
  CheckCircle2,
  Circle,
  Lock,
  Activity,
  Handshake,
  Church,
  Radio,
  Eye,
  Share2,
  ShieldCheck
} from 'lucide-react';

// ============================================================
// MOCK DATA — Wave 1 visual only
// ============================================================
const summary = {
  totalEarned: '$1,240',
  referralEarnings: '$320',
  badgesEarned: 3,
  badgesTotal: 6,
  plaqueLevel: 'Rising Creator',
  plaqueAudience: '64,000 / 100,000',
  freeProStatus: 'In Progress'
};

const badgeCategories = [
  {
    key: 'creation',
    title: 'Creation',
    icon: Sparkles,
    accent: 'pink',
    description: 'Post and ship content.',
    milestones: [
      { label: '5 Posts', state: 'earned' },
      { label: '25 Posts', state: 'in_progress', progress: 48 },
      { label: '100 Posts', state: 'locked' }
    ]
  },
  {
    key: 'execution',
    title: 'Execution',
    icon: Target,
    accent: 'amber',
    description: 'Run the full content pipeline.',
    milestones: [
      { label: '10 Workflows', state: 'earned' },
      { label: '25 Workflows', state: 'in_progress', progress: 56 },
      { label: '50 Workflows', state: 'locked' }
    ]
  },
  {
    key: 'results',
    title: 'Results',
    icon: TrendingUp,
    accent: 'green',
    description: 'Reach and view milestones.',
    milestones: [
      { label: '1,000 Views', state: 'earned' },
      { label: '10,000 Views', state: 'in_progress', progress: 72 },
      { label: '100,000 Views', state: 'locked' }
    ]
  },
  {
    key: 'growth',
    title: 'Growth',
    icon: Rocket,
    accent: 'cyan',
    description: 'Referrals and ecosystem growth.',
    milestones: [
      { label: '1 Click', state: 'earned' },
      { label: '1 Referral', state: 'in_progress', progress: 45 },
      { label: '5 Referrals', state: 'locked' }
    ]
  },
  {
    key: 'consistency',
    title: 'Consistency',
    icon: Flame,
    accent: 'orange',
    description: 'Show-up streaks.',
    milestones: [
      { label: '3 Day Streak', state: 'earned' },
      { label: '7 Day Streak', state: 'in_progress', progress: 60 },
      { label: '30 Day Streak', state: 'locked' }
    ]
  },
  {
    key: 'community',
    title: 'Community',
    icon: Heart,
    accent: 'rose',
    description: 'Engage the ecosystem.',
    milestones: [
      { label: 'Interactions', state: 'in_progress', progress: 32 },
      { label: 'Replies', state: 'locked' },
      { label: 'Posts', state: 'locked' },
      { label: 'Conversations', state: 'locked' }
    ]
  }
];

const creatorBonusLadder = [
  { tier: '10,000', payout: '$5' },
  { tier: '50,000', payout: '$15' },
  { tier: '100,000', payout: '$30' },
  { tier: '250,000', payout: '$75' },
  { tier: '500,000', payout: '$150' },
  { tier: '1,000,000', payout: '$300' }
];

const referralLadder = [
  { tier: '1', payout: '$5' },
  { tier: '3', payout: '$20' },
  { tier: '5', payout: '$40' },
  { tier: '10', payout: '$100' },
  { tier: '25', payout: '$250' }
];

const spotlightPerformance = [
  { tier: '1K viewers', payout: '$25' },
  { tier: '5K', payout: '$75' },
  { tier: '10K', payout: '$150' },
  { tier: '25K', payout: '$400' },
  { tier: '50K', payout: '$1,000' },
  { tier: '100K+', payout: '$2,500 cap' }
];

const spotlightGifts = [
  { tier: '$100 gifts', payout: '$20' },
  { tier: '$500', payout: '$75' },
  { tier: '$1,000', payout: '$150' },
  { tier: '$5,000', payout: '$750' },
  { tier: '$10,000', payout: '$2,000' },
  { tier: '$25,000+', payout: '$5,000 cap' }
];

const plaques = [
  {
    key: 'rising',
    name: 'Rising Creator',
    score: 100,
    scoreProgress: 64,
    audience: '100,000',
    audienceProgress: 64,
    icon: Medal,
    accent: 'pink',
    state: 'in_progress',
    rank: '15th Rising Creator Ever',
    tierId: 'RC-000015',
    globalId: 'CST-000231'
  },
  {
    key: 'breakout',
    name: 'Breakout Creator',
    score: 300,
    scoreProgress: 0,
    audience: '500,000',
    audienceProgress: 0,
    icon: Trophy,
    accent: 'amber',
    state: 'locked'
  },
  {
    key: 'established',
    name: 'Established Creator',
    score: 700,
    scoreProgress: 0,
    audience: '1,000,000',
    audienceProgress: 0,
    icon: Award,
    accent: 'cyan',
    state: 'locked'
  },
  {
    key: 'elite',
    name: 'Elite Creator',
    score: 1500,
    scoreProgress: 0,
    audience: '5,000,000',
    audienceProgress: 0,
    icon: Crown,
    accent: 'violet',
    state: 'locked'
  },
  {
    key: 'icon',
    name: 'Icon Creator',
    score: 3000,
    scoreProgress: 0,
    audience: '10,000,000+',
    audienceProgress: 0,
    icon: Sparkles,
    accent: 'rose',
    state: 'locked'
  }
];

const activity = [
  { id: 'a1', icon: Award, accent: 'amber', title: 'Earned the Execution badge', category: 'Badge Earned', meta: '2h ago' },
  { id: 'a2', icon: Coins, accent: 'pink', title: 'Creator Bonus paid — $30 (100k views)', category: 'Bonus Paid', meta: '1d ago' },
  { id: 'a3', icon: Handshake, accent: 'green', title: 'Referral qualified — @maya', category: 'Referral Qualified', meta: '1d ago' },
  { id: 'a4', icon: Medal, accent: 'pink', title: 'Rising Creator plaque progress +12%', category: 'Plaque Progress', meta: '3d ago' },
  { id: 'a5', icon: Share2, accent: 'cyan', title: 'Shared "7-Day Streak" badge win', category: 'Amplification Shared', meta: '5d ago' }
];

const tier2Eligibility = [
  { key: 'creation_or_execution', label: 'Creation OR Execution', met: true, hint: 'Earned via Execution' },
  { key: 'results', label: 'Results', met: false, hint: '72% to first milestone' },
  { key: 'growth', label: 'Growth', met: false, hint: '45% to 1st referral' },
  { key: 'consistency', label: 'Consistency', met: false, hint: '60% to 7-day streak' },
  { key: 'community', label: 'Community', met: false, hint: '32% to first interaction set' }
];

// ============================================================
// Reusable bits
// ============================================================
const StateBadge = ({ state, label }) => {
  const map = {
    earned: { label: label || 'Earned', cls: 'border-green-500/30 text-green-300 bg-green-500/10' },
    in_progress: { label: label || 'In Progress', cls: 'border-blue-500/30 text-blue-300 bg-blue-500/10' },
    locked: { label: label || 'Locked', cls: 'border-violet-500/30 text-violet-300 bg-violet-500/10' },
    active: { label: label || 'Active', cls: 'border-green-500/30 text-green-300 bg-green-500/10' },
    pending: { label: label || 'Pending Qualification', cls: 'border-amber-500/30 text-amber-300 bg-amber-500/10' },
    available: { label: label || 'Available', cls: 'border-white/15 text-[#C7CED9] bg-white/[0.03]' },
    eligible: { label: label || 'Eligible', cls: 'border-green-500/30 text-green-300 bg-green-500/10' }
  };
  const entry = map[state] || map.available;
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${entry.cls}`}>
      {state === 'locked' && <Lock className="h-2.5 w-2.5 mr-1" />}
      {entry.label}
    </Badge>
  );
};

const TierHeader = ({ tierLabel, tierAccent, title, subtitle }) => (
  <div className="mb-4">
    <div className="flex items-center gap-2 mb-1">
      <span className={`text-[10px] uppercase tracking-wider text-${tierAccent}-300 px-1.5 py-0.5 rounded bg-${tierAccent}-500/10 border border-${tierAccent}-500/30`}>
        {tierLabel}
      </span>
      <h2 className="text-base font-semibold text-white">{title}</h2>
    </div>
    <p className="text-xs text-[#8A93A6]">{subtitle}</p>
  </div>
);

const SummaryCard = ({ icon: Icon, accent, label, value, sublabel, testId }) => (
  <Card className="glass border-white/5" data-testid={testId}>
    <CardContent className="p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-6 h-6 rounded-md bg-${accent}-500/10 border border-white/5 flex items-center justify-center`}>
          <Icon className={`h-3 w-3 text-${accent}-400`} />
        </div>
        <p className="text-[10px] uppercase tracking-wider text-[#8A93A6]">{label}</p>
      </div>
      <p className="text-xl font-bold text-white tabular-nums leading-tight">{value}</p>
      {sublabel && <p className="text-[10px] text-[#8A93A6] mt-0.5">{sublabel}</p>}
    </CardContent>
  </Card>
);

// Badge category card with ordered milestones
const BadgeCategoryCard = ({ b }) => {
  const Icon = b.icon;
  const earnedCount = b.milestones.filter((m) => m.state === 'earned').length;
  const lastEarned = [...b.milestones].reverse().find((m) => m.state === 'earned');
  return (
    <Card className="glass border-white/5 relative overflow-hidden" data-testid={`badge-category-${b.key}`}>
      <CardContent className="p-3">
        <div className="flex items-start gap-2.5 mb-2.5">
          <div className={`w-9 h-9 rounded-lg bg-${b.accent}-500/10 border border-white/5 flex items-center justify-center shrink-0`}>
            <Icon className={`h-4 w-4 text-${b.accent}-400`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white leading-tight">{b.title}</p>
            <p className="text-[10px] text-[#8A93A6] truncate">{b.description}</p>
          </div>
        </div>

        <div className="space-y-1">
          {b.milestones.map((m, idx) => (
            <div
              key={`${b.key}-${idx}`}
              className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ${
                m.state === 'earned'
                  ? 'border-green-500/20 bg-green-500/[0.05]'
                  : m.state === 'in_progress'
                  ? 'border-blue-500/20 bg-blue-500/[0.04]'
                  : 'border-white/5 bg-white/[0.02]'
              }`}
            >
              {m.state === 'earned' ? (
                <CheckCircle2 className="h-3 w-3 text-green-400 shrink-0" />
              ) : m.state === 'in_progress' ? (
                <Circle className="h-3 w-3 text-blue-400 shrink-0" />
              ) : (
                <Lock className="h-2.5 w-2.5 text-[#555E6E] shrink-0" />
              )}
              <span className={`text-[11px] flex-1 truncate ${m.state === 'locked' ? 'text-[#8A93A6]' : 'text-white'}`}>
                {m.label}
              </span>
              {m.state === 'in_progress' && typeof m.progress === 'number' && (
                <>
                  <Progress value={m.progress} className="h-1 w-12 bg-white/[0.04]" />
                  <span className="text-[9px] tabular-nums text-[#8A93A6] w-7 text-right">{m.progress}%</span>
                </>
              )}
              {m.state === 'earned' && <span className="text-[9px] uppercase tracking-wider text-green-300">Earned</span>}
            </div>
          ))}
        </div>

        {earnedCount > 0 && (
          <ShareWinPill
            accent={b.accent}
            label="Share Badge Win"
            preview={`I just hit ${lastEarned.label} on Consistency.`}
            testId={`badge-share-${b.key}`}
          />
        )}
      </CardContent>
    </Card>
  );
};

// Payout ladder strip used inside Tier 2 cards
const PayoutLadder = ({ items, leftLabel, rightLabel }) => (
  <div className="rounded-lg border border-white/5 bg-white/[0.02] overflow-hidden">
    <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/5 bg-white/[0.02]">
      <span className="text-[9px] uppercase tracking-wider text-[#8A93A6]">{leftLabel}</span>
      <span className="text-[9px] uppercase tracking-wider text-[#8A93A6]">{rightLabel}</span>
    </div>
    <div>
      {items.map((row, idx) => (
        <div
          key={idx}
          className="flex items-center justify-between px-3 py-1.5 text-xs border-b border-white/[0.03] last:border-0"
        >
          <span className="text-[#C7CED9] tabular-nums">{row.tier}</span>
          <span className="font-semibold text-pink-300 tabular-nums">{row.payout}</span>
        </div>
      ))}
    </div>
  </div>
);

// Plaque card — luxury treatment (compact)
const PlaqueCard = ({ p }) => {
  const Icon = p.icon;
  const isLocked = p.state === 'locked';
  return (
    <Card
      className={`relative overflow-hidden border ${
        isLocked
          ? 'border-violet-500/15 bg-gradient-to-br from-violet-500/[0.04] via-transparent to-transparent'
          : `border-${p.accent}-500/25 bg-gradient-to-br from-${p.accent}-500/[0.08] via-transparent to-transparent ring-1 ring-${p.accent}-500/15`
      }`}
      data-testid={`plaque-${p.key}`}
    >
      {!isLocked && (
        <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-${p.accent}-500/10 blur-2xl pointer-events-none`} />
      )}
      <CardContent className="p-3 relative">
        <div className="flex items-start justify-between mb-2.5">
          <div className="flex items-center gap-2.5">
            <div className={`w-11 h-11 rounded-xl bg-${p.accent}-500/10 border border-white/10 flex items-center justify-center ${isLocked ? 'opacity-60' : ''}`}>
              <Icon className={`h-5 w-5 text-${p.accent}-400`} />
            </div>
            <p className={`text-sm font-bold ${isLocked ? 'text-[#8A93A6]' : 'text-white'}`}>{p.name}</p>
          </div>
          <StateBadge state={p.state} />
        </div>

        <div className="grid grid-cols-2 gap-2 mb-2">
          <div className="rounded-md border border-white/5 bg-white/[0.02] px-2 py-1.5">
            <p className="text-[9px] uppercase tracking-wider text-[#8A93A6] mb-0.5">Score</p>
            <p className="text-sm font-semibold text-white tabular-nums">{p.score.toLocaleString()}</p>
            <Progress value={p.scoreProgress} className="h-1 mt-1 bg-white/[0.04]" />
          </div>
          <div className="rounded-md border border-white/5 bg-white/[0.02] px-2 py-1.5">
            <p className="text-[9px] uppercase tracking-wider text-[#8A93A6] mb-0.5">Audience</p>
            <p className="text-sm font-semibold text-white tabular-nums">{p.audience}</p>
            <Progress value={p.audienceProgress} className="h-1 mt-1 bg-white/[0.04]" />
          </div>
        </div>

        {p.state === 'in_progress' && p.rank && (
          <>
            <div className="space-y-0.5 text-[10px] mb-1">
              <div className="flex items-center justify-between">
                <span className="text-[#8A93A6]">Rank</span>
                <span className={`text-${p.accent}-300 font-medium`}>{p.rank}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#8A93A6]">Global ID</span>
                <span className="text-white font-mono tabular-nums">{p.globalId}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#8A93A6]">Tier ID</span>
                <span className="text-white font-mono tabular-nums">{p.tierId}</span>
              </div>
            </div>
            <ShareWinPill
              accent={p.accent}
              label="Share Achievement"
              preview={`I just became the ${p.rank} on Consistency.`}
              attachments={['Plaque', 'Unboxing', 'Card']}
              testId={`plaque-share-${p.key}`}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Compact inline share pill — used inside each tier
const ShareWinPill = ({ accent = 'pink', label = 'Share Win', preview, attachments = [], testId }) => (
  <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={testId}>
    <button
      type="button"
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-${accent}-500/10 border border-${accent}-500/25 text-[10px] font-medium text-${accent}-200 hover:bg-${accent}-500/15 transition-colors`}
    >
      <Share2 className="h-3 w-3" />
      {label}
    </button>
    {preview && (
      <span className="text-[10px] text-[#8A93A6] truncate flex-1 min-w-0 italic">
        “{preview}”
      </span>
    )}
    {attachments.length > 0 && (
      <div className="flex items-center gap-1">
        {attachments.map((a) => (
          <span
            key={a}
            className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/5 text-[#8A93A6]`}
          >
            {a}
          </span>
        ))}
      </div>
    )}
  </div>
);

// ============================================================
// MAIN
// ============================================================
export default function Rewards() {
  const { user } = useContext(AuthContext);
  const isAdmin = !!user?.is_internal_admin;
  const tier2Met = tier2Eligibility.filter((e) => e.met).length;
  const tier2Total = tier2Eligibility.length;
  const tier2Unlocked = tier2Met === tier2Total;

  return (
    <div className="container mx-auto p-6 max-w-6xl" data-testid="rewards-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Trophy className="h-6 w-6 text-amber-400" />
          <h1 className="text-3xl font-bold text-white">Rewards</h1>
          <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-300 bg-amber-500/10 uppercase tracking-wider font-semibold">
            Verified Status + Growth Engine
          </Badge>
          {isAdmin && (
            <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider font-semibold">
              Admin Preview
            </Badge>
          )}
        </div>
        <p className="text-[#8A93A6] max-w-2xl">
          Earn badges, unlock bonuses, achieve verified plaques, and amplify wins. Rewards activate from Day 1 of Pre-Sales.
        </p>
      </div>

      {/* 1. Rewards Overview */}
      <section className="mb-10" data-testid="rewards-overview">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <SummaryCard icon={Coins} accent="green" label="Total Rewards Earned" value={summary.totalEarned} testId="summary-total-earned" />
          <SummaryCard icon={Handshake} accent="cyan" label="Referral Earnings" value={summary.referralEarnings} testId="summary-referral" />
          <SummaryCard icon={Award} accent="amber" label="Badge Progress" value={`${summary.badgesEarned} / ${summary.badgesTotal}`} sublabel="Next: Results" testId="summary-badge" />
          <SummaryCard icon={Medal} accent="pink" label="Plaque Progress" value={summary.plaqueLevel} sublabel={summary.plaqueAudience} testId="summary-plaque" />
          <SummaryCard icon={Gift} accent="violet" label="Free Pro Month" value={summary.freeProStatus} sublabel="Complete tasks to unlock" testId="summary-free-pro" />
        </div>
      </section>

      {/* 2. Tier 1 — Badges */}
      <section className="mb-10" data-testid="rewards-tier-badges">
        <TierHeader
          tierLabel="Tier 1"
          tierAccent="amber"
          title="Badges — Behavior Engine"
          subtitle="Drive activity, build consistency, and unlock Tier 2."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {badgeCategories.map((b) => (
            <BadgeCategoryCard key={b.key} b={b} />
          ))}
        </div>
      </section>

      {/* 3. Tier 2 — Bonus Center */}
      <section className="mb-10" data-testid="rewards-tier-bonuses">
        <TierHeader
          tierLabel="Tier 2"
          tierAccent="pink"
          title="Bonus Center — Monetization Engine"
          subtitle="Performance, referral, and creator incentives."
        />

        {/* Tier 2 Unlock Progress — compact inline card */}
        <Card className="glass border-white/5 mb-4" data-testid="tier2-unlock-progress">
          <CardContent className="p-3">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" />
                <p className="text-sm font-semibold text-white">Tier 2 Unlock Progress</p>
              </div>
              {tier2Unlocked ? (
                <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-300 bg-green-500/10 uppercase tracking-wider">
                  Tier 2 Unlocked
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-300 bg-amber-500/10 uppercase tracking-wider">
                  {tier2Met} / {tier2Total} Qualified
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
              {tier2Eligibility.map((e) => (
                <div
                  key={e.key}
                  className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 ${
                    e.met ? 'border-green-500/25 bg-green-500/[0.06]' : 'border-white/5 bg-white/[0.02]'
                  }`}
                  data-testid={`tier2-elig-${e.key}`}
                >
                  {e.met ? (
                    <CheckCircle2 className="h-3 w-3 text-green-400 shrink-0" />
                  ) : (
                    <Circle className="h-3 w-3 text-[#555E6E] shrink-0" />
                  )}
                  <span className={`text-[11px] truncate ${e.met ? 'text-white' : 'text-[#8A93A6]'}`}>{e.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* A) Creator Bonus */}
          <Card className="glass border-white/5" data-testid="bonus-creator">
            <CardContent className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-pink-500/10 border border-white/5 flex items-center justify-center">
                    <Award className="h-4 w-4 text-pink-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white leading-tight">Creator Bonus</p>
                    <p className="text-[10px] text-[#8A93A6]">Performance-based incentives.</p>
                  </div>
                </div>
                <StateBadge state="active" />
              </div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] border-white/15 text-[#C7CED9] bg-white/[0.03] uppercase tracking-wider">
                  Weekly payout
                </Badge>
                <span className="text-[10px] text-[#8A93A6]">72% to next tier</span>
              </div>
              <PayoutLadder items={creatorBonusLadder} leftLabel="Views" rightLabel="Payout" />
              <ShareWinPill
                accent="pink"
                label="Share Creator Win"
                preview="I just crossed 100,000 views using Consistency."
                testId="creator-bonus-share"
              />
            </CardContent>
          </Card>

          {/* B) Referral Bonus */}
          <Card className="glass border-white/5" data-testid="bonus-referral">
            <CardContent className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-white/5 flex items-center justify-center">
                    <Handshake className="h-4 w-4 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white leading-tight">Referral Bonus</p>
                    <p className="text-[10px] text-[#8A93A6]">Earn rewards for referrals.</p>
                  </div>
                </div>
                <StateBadge state="in_progress" />
              </div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] border-white/15 text-[#C7CED9] bg-white/[0.03] uppercase tracking-wider">
                  Weekly payout
                </Badge>
                <span className="text-[10px] text-[#8A93A6]">Current: 2 qualified</span>
              </div>
              <PayoutLadder items={referralLadder} leftLabel="Referrals" rightLabel="Payout" />
              <ShareWinPill
                accent="cyan"
                label="Share Creator Win"
                preview="I just earned my first payout from Consistency."
                testId="referral-bonus-share"
              />
            </CardContent>
          </Card>

          {/* C) Creator Spotlight Bonus */}
          <Card className="glass border-amber-500/20 ring-1 ring-amber-500/10" data-testid="bonus-spotlight">
            <CardContent className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-white/5 flex items-center justify-center">
                    <Radio className="h-4 w-4 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white leading-tight">Creator Spotlight Bonus</p>
                    <p className="text-[10px] text-[#8A93A6]">Live creator interview rewards.</p>
                  </div>
                </div>
                <StateBadge state="available" />
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-300 bg-green-500/10 uppercase tracking-wider">
                  <ShieldCheck className="h-2.5 w-2.5 mr-1" />
                  System verified
                </Badge>
                <Badge variant="outline" className="text-[10px] border-white/15 text-[#C7CED9] bg-white/[0.03] uppercase tracking-wider">
                  60-min minimum
                </Badge>
              </div>

              {/* Base payout */}
              <div className="rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-1.5 mb-1.5">
                <p className="text-[9px] uppercase tracking-wider text-[#8A93A6] mb-0.5">Base payout</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#C7CED9]">Interviewer</span>
                  <span className="font-semibold text-amber-300 tabular-nums">$50</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#C7CED9]">Interviewee</span>
                  <span className="font-semibold text-amber-300 tabular-nums">$50</span>
                </div>
              </div>

              <p className="text-[9px] uppercase tracking-wider text-[#8A93A6] mb-1">Performance bonus</p>
              <div className="mb-1.5">
                <PayoutLadder items={spotlightPerformance} leftLabel="Viewers" rightLabel="Bonus" />
              </div>

              <p className="text-[9px] uppercase tracking-wider text-[#8A93A6] mb-1">Gift bonus</p>
              <PayoutLadder items={spotlightGifts} leftLabel="Gifts" rightLabel="Bonus" />

              <ShareWinPill
                accent="amber"
                label="Share Creator Win"
                preview="I just hosted a Creator Spotlight on Consistency."
                testId="spotlight-bonus-share"
              />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 4. Tier 3 — Plaques */}
      <section className="mb-10" data-testid="rewards-tier-plaques">
        <TierHeader
          tierLabel="Tier 3"
          tierAccent="cyan"
          title="Plaques — Verified Status System"
          subtitle="Verified creator status based on audience and achievement score."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plaques.map((p) => (
            <PlaqueCard key={p.key} p={p} />
          ))}
        </div>
      </section>

      {/* 5. Recent Wins (renamed from Rewards Activity) */}
      <section className="mb-10" data-testid="recent-wins">
        <Card className="glass border-white/5">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <Activity className="h-3.5 w-3.5 text-cyan-400" />
              Recent Wins
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {activity.map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.id}
                  className="flex items-center gap-2.5 px-4 py-1.5 border-b border-white/[0.04] last:border-0"
                  data-testid={`activity-${a.id}`}
                >
                  <div className={`w-7 h-7 rounded-full bg-${a.accent}-500/10 border border-white/5 flex items-center justify-center shrink-0`}>
                    <Icon className={`h-3 w-3 text-${a.accent}-400`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white truncate leading-snug">{a.title}</p>
                    <p className="text-[10px] text-[#8A93A6]">{a.meta}</p>
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-[9px] uppercase tracking-wider border-${a.accent}-500/30 text-${a.accent}-300 bg-${a.accent}-500/10`}
                  >
                    {a.category}
                  </Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </section>

      {/* 8. Connected Reward Programs */}
      <section data-testid="rewards-connections">
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-white">Connected Reward Programs</h2>
            <p className="text-xs text-[#8A93A6]">Affiliate and partnership systems linked to your Rewards layer.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="glass border-white/5" data-testid="connection-affiliates">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-pink-500/10 border border-white/5 flex items-center justify-center">
                    <Users2 className="h-5 w-5 text-pink-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Affiliates</p>
                    <p className="text-[11px] text-[#8A93A6]">Earn through referrals and growth.</p>
                  </div>
                </div>
                <StateBadge state="eligible" />
              </div>
              <div className="flex items-center gap-2 text-[11px] text-[#8A93A6]">
                <Eye className="h-3 w-3" />
                Linked to your subscription · Weekly incentive payouts
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-white/5" data-testid="connection-church-partner">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-white/5 flex items-center justify-center">
                    <Church className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Church Partner</p>
                    <p className="text-[11px] text-[#8A93A6]">Faith-based referral and partnership program.</p>
                  </div>
                </div>
                <StateBadge state="available" />
              </div>
              <div className="flex items-center gap-2 text-[11px] text-[#8A93A6]">
                <Heart className="h-3 w-3" />
                Available to all creators · Weekly incentive payouts
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
